import { Grid, Stack } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { WarningAmber } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import moment from 'moment';
import PropTypes from 'prop-types';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { GET_SPRINT_DETAIL, ADD_DURATION_DATE } from '../../apis/sprint';
import Button from '../atoms/button';
import DatePicker from './DatePicker';
import ProjectContext from '../../context/ProjectContext';
import SprintSettings from '../../images/sprintSetting.svg';
import useProjectVersionHistory from '../../context/VersionHistoryContext';

const propTypes = {
  projectId: PropTypes.string,
};
const defaultProps = {
  projectId: '',
};

const SprintDialog = ({ projectId }) => {
  const { selectedListItem, undoEdits, redoEdits, setUndoEdits } = useProjectVersionHistory();
  const { dispatch } = useContext(ProjectContext);

  const [open, setOpen] = useState(false);
  const [weeks, setWeeks] = useState();
  const [date, setDate] = useState();
  const [sprint, setSprint] = useState([]);
  const [loading, setIsLoading] = useState();
  const [data, setData] = useState();

  const [getSprintDetail] = useLazyQuery(GET_SPRINT_DETAIL, { fetchPolicy: 'network-only', variables: { input: { project_id: projectId } } });
  const [addSprintDurationDate] = useMutation(ADD_DURATION_DATE);

  const sprintDetail = async () => {
    const { loading: sprintLoading, data: sprintData } = await getSprintDetail();
    setData(sprintData);
    setIsLoading(sprintLoading);
  };

  useEffect(() => {
    if ((undoEdits || redoEdits) && !(undoEdits === redoEdits)) {
      sprintDetail();
    }
  }, [undoEdits, redoEdits]);

  useEffect(() => {
    if (!loading && data) {
      setSprint(data.sprint);
      setWeeks(sprint?.duration);
      setDate(sprint?.start_date);
    }
  }, [data, loading, sprint?.length, selectedListItem, undoEdits, redoEdits]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setWeeks(sprint?.duration);
    setDate(sprint?.start_date);
  };

  const onRadioButtonChange = (e) => {
    setWeeks(e.target.value);
  };
  const onDateSelected = (event) => {
    setDate(event);
  };
  const saveSprint = async () => {
    try {
      const variables = { input: { project_id: projectId, duration: parseInt(weeks, 10), start_date: moment(date).format('YYYY-MM-DD') } };

      const sprintData = await addSprintDurationDate({ variables });

      if (!undoEdits) setUndoEdits(true);

      setSprint(data !== null && sprintData?.data?.addSprint);

      const duration =
        data?.sprint?.duration === sprint?.duration || data?.sprint?.duration === undefined
          ? weeks
          : sprint.duration;
      const startDate =
        data?.sprint?.start_date === sprint?.start_date || data?.sprint?.start_date === undefined
          ? date
          : sprint?.start_date;

      setWeeks(duration);
      setDate(startDate);

      if (sprintData) {
        setOpen(false);
        const { duration: sprintDuration, start_date: sprintStartDate } = sprintData.data.addSprint;
        dispatch({ type: 'SET_PROJECT_DETAIL', payload: { startDate: sprintStartDate, duration: sprintDuration } });
      }

      sprintDetail();
    } catch (e) {
      setOpen(true);
      setWeeks('');
    }
  };

  const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  const addWeeksDate = new Date(date);
  addWeeksDate.setDate(addWeeksDate.getDate() + weeks * 7);
  const formattedDate = addWeeksDate.toLocaleDateString('en-US', options);

  const message = useMemo(() => {
    let alertText = '';
    if (date !== null && date !== undefined && weeks !== undefined && weeks !== null && formattedDate !== 'Invalid Date') {
      alertText = `This sprint ends ${formattedDate}`;
    } else if (date !== undefined && date !== null && (weeks === undefined || weeks === null)) {
      alertText = 'Please set sprint duration to estimate end date !!';
    } else if (weeks !== null && weeks !== undefined && (date === undefined || date === null)) {
      alertText = 'Please set sprint start date to estimate end date !!';
    } else {
      alertText = 'Please set sprint duration and start date to estimate end date !!';
    }
    return alertText;
  }, [date, weeks, formattedDate]);

  return (
    <Grid>
      <Grid container>
        <Button icon primary outlinedColor className="btn-sprint" onClick={handleClickOpen}>
          <img width={24} height={24} src={SprintSettings} alt="sprint-settings" />
        </Button>
        <Dialog
          className="custom-modal-dialog sprint-modal-dialog"
          open={open}
          onClose={handleClose}
          aria-describedby="alert-dialog-description"
          aria-labelledby="responsive-dialog-title"
        >
          <DialogTitle id="responsive-dialog-title">Sprint - 1 Settings</DialogTitle>

          <DialogContent className="dialog-content">
            {sprint !== null && sprint?.start_date !== null && sprint?.duration !== null && (
              <div className="alert-notify">
                <Alert icon={<WarningAmber />}>
                  This project has started; changing these settings may update the project timeline
                </Alert>
              </div>
            )}
            <FormControl className="radio-main">
              <FormLabel className="sprint-duration-title">
                Sprint duration :
              </FormLabel>

              <RadioGroup
                sx={{ paddingTop: 2 }}
                className="radio-group"
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                onChange={onRadioButtonChange}
                defaultValue={sprint?.duration}
              >
                <Stack direction="row" spacing={4}>
                  <FormControlLabel
                    className="custom-radio"
                    value="1"
                    control={<Radio />}
                    label="1 week"
                  />
                  <FormControlLabel
                    className="custom-radio"
                    value="2"
                    control={<Radio />}
                    label="2 weeks"
                  />
                  <FormControlLabel
                    className="custom-radio"
                    value="3"
                    control={<Radio />}
                    label="3 weeks"
                  />
                  <FormControlLabel
                    className="custom-radio"
                    value="4"
                    control={<Radio />}
                    label="4 weeks"
                  />
                </Stack>
              </RadioGroup>
            </FormControl>

            <Grid>
              <DialogContentText className="sprint-duration-title">
                Sprint start date :
              </DialogContentText>

              <Grid item mt={2}>
                <DatePicker
                  onChange={onDateSelected}
                  data={date}
                  selectDate={sprint?.start_date !== null ? new Date(sprint?.start_date) : null}
                />
              </Grid>
              <Grid item mt={2}>
                <DialogContentText className="date-info">{message}</DialogContentText>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className="dialog-actions" sx={{ padding: '25px' }}>
            <Button primary outlined onClick={handleClose}>Cancel</Button>
            <Button primary onClick={saveSprint}>Save</Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
};

SprintDialog.propTypes = propTypes;
SprintDialog.defaultProps = defaultProps;
export default SprintDialog;
